import {
  Sparkle, Crown, Briefcase, Buildings, MapPinLine,
  Lightning, Shield, ChartLineUp, Receipt,
} from "@phosphor-icons/react";

export const CONTACT_EMAIL = "jjp.highvibepinnacles89@gmail.com";

// Map UI tier id -> backend package_id. Enterprise has no package — stays a mailto.
export const TIER_TO_PACKAGE = {
  launch: "launch",
  ramp: "ramp",
  growth: "growth",
  branded: "branded",
  territory: "territory",
  pilot: "pilot",
};

export const MAIN_TIERS = [
  {
    id: "launch",
    eyebrow: "First 60 days · Ramp 1 of 3",
    icon: Briefcase,
    name: "New Advisor Launch",
    price: "$797",
    unit: "/month minimum",
    plus: "Includes $500 prepaid lead credit · $250 per qualified lead",
    description:
      "For newer IBC advisors who need help getting into serious business-owner conversations without being forced into the full Growth plan on day one. A ramp plan, not a discount plan.",
    commitment: { platform: "$297/mo", leadCredit: "$500/mo prepaid", total: "$797/mo" },
    leadPricing: {
      title: "Lead pricing — first 60 days",
      items: [
        "$250 per qualified lead",
        "Unused lead credits roll forward for up to 30 days",
        "Additional qualified leads billed at $250 each",
      ],
    },
    included: [
      "Advisor calculator access",
      "Public quick-breakdown link",
      "Basic lead capture",
      "Advisor dashboard",
      "Standard Build A Bank report format",
      "Monthly product updates",
      "Basic onboarding resources",
      "New-advisor follow-up scripts",
      "Basic lead-handling guidance",
    ],
    bestFor: [
      "New IBC advisors",
      "Solo advisors testing a business-owner niche",
      "Advisors who want a stronger discovery tool before full white-label",
    ],
    important:
      "This plan automatically steps up to Advisor Ramp after the first 60 days. It is designed to help new advisors launch, not to create a permanent low-price plan.",
    ctaLabel: "Start New Advisor Launch",
  },
  {
    id: "ramp",
    eyebrow: "Months 3 – 6 · Ramp 2 of 3",
    icon: ChartLineUp,
    name: "Advisor Ramp",
    price: "$1,347",
    unit: "/month minimum",
    plus: "Includes $750 prepaid lead credit · $200 per qualified lead",
    description:
      "For advisors who have completed the first 60 days and are ready to turn the platform into a more consistent lead-generation and sales-conversation channel.",
    commitment: { platform: "$597/mo", leadCredit: "$750/mo prepaid", total: "$1,347/mo" },
    leadPricing: {
      title: "Lead pricing — months 3 to 6",
      items: [
        "$200 per qualified lead",
        "Unused lead credits roll forward for up to 30 days",
        "Additional qualified leads billed at $200 each",
      ],
    },
    included: [
      "Everything in New Advisor Launch",
      "Enhanced lead dashboard",
      "Better lead-source tracking",
      "Follow-up workflow recommendations",
      "Conversion-focused advisor positioning",
      "Monthly performance check-in",
    ],
    bestFor: [
      "Advisors actively following up with leads",
      "Advisors who want to prove ROI before moving into the full Growth plan",
      "Advisors who need structure during their first six months",
    ],
    important:
      "This plan automatically steps up to Advisor Growth after month 6 unless another plan is selected.",
    ctaLabel: "Continue Into Advisor Ramp",
  },
  {
    id: "growth",
    eyebrow: "Month 7 onward · Most popular",
    icon: Sparkle,
    name: "Advisor Growth",
    price: "$1,997",
    unit: "/month minimum",
    plus: "Includes $1,000 prepaid lead credit · $150 per qualified lead",
    highlight: true,
    description:
      "For advisors who want Build A Bank to become a serious lead-generation and sales-conversion channel.",
    commitment: { platform: "$997/mo", leadCredit: "$1,000/mo prepaid", total: "$1,997/mo" },
    leadPricing: {
      title: "Lead pricing — month 7 onward",
      items: [
        "$150 per qualified lead",
        "Unused lead credits roll forward for up to 30 days",
        "Additional qualified leads billed at $150 each",
      ],
    },
    included: [
      "Everything in Advisor Ramp",
      "Priority lead routing",
      "Enhanced lead dashboard",
      "Business-owner quick-breakdown funnel",
      "Lead source tracking (?ref= attribution)",
      "Follow-up workflow recommendations",
      "Conversion-focused advisor positioning",
      "Monthly performance review",
    ],
    bestFor: [
      "Advisors who actively sell IBC",
      "Advisors targeting contractors, trades, fleet-heavy businesses",
      "Advisors who want more problem-aware prospects",
    ],
    note: "Qualified lead = business owner or decision-maker who completes the quick breakdown, submits valid contact information, and provides enough business information to support a legitimate advisor follow-up.",
    ctaLabel: "Apply for Advisor Growth",
  },
];

export const PARTNER_TIERS = [
  {
    id: "branded",
    eyebrow: "Branded",
    icon: Crown,
    name: "Branded Pro",
    price: "$2,500",
    unit: " setup + $1,500/mo",
    description:
      "For advisors who want a branded calculator experience under their own firm identity.",
    included: [
      "Everything in Advisor Growth",
      "Custom branding throughout",
      "Advisor or firm logo on every export",
      "Branded report styling",
      "Custom domain or subdomain setup",
      "White-label client experience",
      "Branded PDF/report output",
      "Dedicated onboarding session",
      "Priority support",
    ],
    bestFor: [
      "Established IBC advisors",
      "Advisors with an existing audience",
      "Firms that want a more professional client-facing funnel",
    ],
    ctaLabel: "Book Branded Pro Setup",
  },
  {
    id: "territory",
    eyebrow: "Territory exclusive",
    icon: MapPinLine,
    name: "Territory Partner",
    price: "From $3,000",
    unit: "/month",
    description:
      "For serious advisors who want priority positioning in a market, niche, or business-owner category.",
    included: [
      "Everything in Branded Pro",
      "Priority market placement",
      "Territory or niche-based routing options",
      "Advanced reporting",
      "Custom funnel strategy",
      "Quarterly growth review",
      "Optional exclusivity terms by region or vertical",
    ],
    bestFor: [
      "Experienced IBC advisors",
      "Multi-advisor firms",
      "Advisors focused on contractors, real estate investors, fleet-heavy businesses",
      "Advisors who want a defensible local or niche advantage",
    ],
    ctaLabel: "Discuss Territory Availability",
  },
  {
    id: "enterprise",
    eyebrow: "Firm license",
    icon: Buildings,
    name: "Enterprise / Firm",
    price: "Custom",
    unit: " pricing",
    description:
      "For agencies, IMOs, BGAs, and firms that want to deploy Build A Bank across multiple advisors.",
    included: [
      "Multi-advisor access",
      "Admin controls",
      "Firm-level reporting",
      "Advisor-level dashboards",
      "Custom onboarding",
      "Custom branding",
      "Lead routing rules",
      "Team training options",
    ],
    bestFor: ["IMOs / BGAs", "Multi-advisor agencies", "Firms with 5+ advisors selling IBC"],
    ctaLabel: "Request Firm Pricing",
  },
];

export const UPGRADES = [
  {
    id: "upgrade-whitelabel",
    icon: Crown,
    eyebrow: "Add-on · Branding",
    name: "Basic White-Label Upgrade",
    price: "$500",
    unit: " setup + $200/mo",
    description:
      "A lighter-touch white-label option for advisors who want their name and firm front-and-center on client-facing outputs without the full Branded Pro buildout.",
    included: [
      "Your name and firm on every PDF export",
      "Your logo on advisor reports",
      "Branded email footer on lead notifications",
      "Custom advisor profile shown on the quick-breakdown",
      "Keeps the Build A Bank URL — no custom domain",
    ],
    bestFor: [
      "Newer advisors who want brand polish without setup overhead",
      "Advisors testing the platform before committing to Branded Pro",
      "Solo advisors who don't need a custom domain yet",
    ],
    note: "Stackable on Launch, Ramp, or Growth plans. Upgrade to Branded Pro any time and we credit the setup fee.",
  },
  {
    id: "upgrade-territory",
    icon: MapPinLine,
    eyebrow: "Add-on · Exclusivity",
    name: "Territory Exclusivity",
    price: "From $750",
    unit: "/month add-on",
    description:
      "Lock down a region, niche, or business-owner vertical so leads from that segment route to you first — without going all-in on the full Territory Partner agreement.",
    included: [
      "Priority routing for one region or vertical",
      "First right of refusal on inbound leads in that segment",
      "Listed as the featured advisor for that niche",
      "Quarterly territory performance review",
      "Optional 12-month exclusivity term",
    ],
    bestFor: [
      "Advisors focused on contractors, trades, or fleet-heavy verticals",
      "Advisors with a specific city, region, or niche they want to own",
      "Established advisors who want a defensible local advantage",
    ],
    note: "Availability is limited per region/vertical. Stackable on Ramp, Growth, or Branded Pro plans.",
  },
];

export const PROBLEM_CARDS = [
  {
    icon: Receipt,
    title: "Where wealth leaks",
    body: "Inefficient cash flow, fleet financing to outside lenders, and underused retained earnings — Build A Bank quantifies it in CAD.",
  },
  {
    icon: ChartLineUp,
    title: "What's being missed",
    body: "The 10-year wealth the owner is leaving on the table by relying only on banks, lenders, and traditional savings structures.",
  },
  {
    icon: Shield,
    title: "Why an IBC advisor",
    body: "The platform lays out the gap. You walk in as the licensed expert who designs a real strategy around their actual business.",
  },
];

export const VALUE_CARDS = [
  {
    icon: Lightning,
    title: "A calculator that creates urgency",
    body: "Turns everyday business numbers into a visual wealth-gap breakdown. You start with the owner's problem — money leaving the business unnecessarily — not with insurance.",
  },
  {
    icon: Sparkle,
    title: "A lead magnet built for IBC",
    body: "The public quick-breakdown link lets prospects run a short self-serve analysis. When they finish, the lead flows into your dashboard for follow-up while the problem is fresh.",
  },
  {
    icon: Buildings,
    title: "Advisor-led sessions",
    body: "Use the full calculator live with business owners — walk through tax drag, interest recapture, retention planning, and long-term funding potential together.",
  },
  {
    icon: Crown,
    title: "Branded client reports",
    body: "Polished, professional client-facing PDFs with your name, firm, and contact — built to support the next conversation, not replace it.",
  },
  {
    icon: ChartLineUp,
    title: "A better first conversation",
    body: 'Instead of "Do you want to talk about life insurance?" — ask "Would you like to see how much capital your business may be leaking over the next 10 years?"',
  },
  {
    icon: Shield,
    title: "Compliance-aware framing",
    body: "Outputs are framed as educational estimates and conversation starters — designed to support, not substitute for, your suitability review and product design.",
  },
];

export const PILOT_TERMS = [
  "$1 for 7 days",
  "No long-term commitment during pilot",
  "Limited calculator access",
  "No white-label setup during pilot",
  "Upgrade required for lead routing and branded reports",
];

export const ROI_ITEMS = [
  "More business-owner conversations",
  "Better first calls",
  "More urgency around cash-flow leakage",
  "A clearer reason to book an advisor session",
  "A more professional client experience",
  "A repeatable funnel for IBC education",
];

export const COMPARISON_ROWS = [
  ["Advisor explains everything manually", "Prospect sees the gap before the call"],
  ["No public lead capture", "Public quick-breakdown link captures leads"],
  ["Hard to share professionally", "Branded reports and advisor dashboard"],
  ["No routing or follow-up workflow", "Leads flow into your dashboard"],
  ["Feels technical", "Feels like a business-owner diagnostic"],
];

export const FAQS = [
  {
    q: "Is this only for Nelson Nash-style IBC advisors?",
    a: "Build A Bank is built for advisors who believe in properly structured, cash-value-focused IBC conversations. It's especially useful for advisors who educate business owners, contractors, trades, real estate investors, and incorporated professionals — but the tool itself is product-agnostic.",
  },
  {
    q: "Does the app sell insurance for me?",
    a: "No. The app doesn't replace the advisor. It helps create a better business case, captures interested prospects, and directs them toward a real advisor conversation. You handle suitability, illustration, product design, and the close.",
  },
  {
    q: "What counts as a qualified lead?",
    a: "A qualified lead is a business owner or decision-maker who completes the quick breakdown, submits valid contact information, and provides enough business data to justify a legitimate follow-up. Duplicate submissions, obvious bots, and invalid contact info don't count.",
  },
  {
    q: "Can I use my own brand?",
    a: "Yes — Branded Pro and Territory Partner plans include custom branding. This covers your logo, styling, report format, and a custom domain or subdomain so the entire client experience feels like your own product.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Advisor Starter and Advisor Growth plans are monthly after the initial onboarding period. Branded Pro and Territory Partner require a minimum term because of the setup, branding, and market-positioning work involved.",
  },
  {
    q: "Do you guarantee closed clients?",
    a: "No platform can guarantee closed insurance business. Build A Bank helps create better conversations and better-qualified opportunities — but conversion depends on advisor follow-up, licensing, compliance, suitability, product design, and sales process. The platform amplifies your effort, it doesn't replace it.",
  },
  {
    q: "What about compliance for the calculator outputs?",
    a: "Every results page, PDF, and shared link includes a hypothetical-illustration disclaimer, clarifying that dividends are not guaranteed, that tax assumptions may change, and that clients must obtain independent professional advice. The platform is positioned as an educational conversation starter — never as financial advice.",
  },
  {
    q: "How do I get started?",
    a: 'Start with the $1 pilot to see the platform in action with your own clients. Email jjp.highvibepinnacles89@gmail.com or click "Start Advisor Pilot" anywhere on this page.',
  },
];
